import React, { useState, useContext } from 'react';
import { 
  Container, Typography, TextField, Button, Box, 
  Card, CardContent, Grid, Alert,
  CircularProgress
} from '@mui/material';
import apiClient from '../api/apiClient';
import { AuthContext } from '../context/AuthContext';
import PersonIcon from '@mui/icons-material/Person';

export default function ProfileUpdate() {
  const { user, login, token } = useContext(AuthContext);
  const [name, setName] = useState(user?.name || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    if (name.length > 100) {
      setError('Name must be 100 characters or less');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await apiClient.put('/users/profile', { name: name.trim() });
      
      // Update the user data in AuthContext with the new name
      const updatedUser = { ...user, name: response.data.name };
      login(updatedUser, token);
      
      setSuccess('Profile updated successfully!');
    } catch (err) {
        console.error("Failed to update profile:", err);
        if (err.response && err.response.data) {
            const problem = err.response.data;
            setError(problem.detail || problem.title || 'Failed to update profile.');
        } else {
            setError('Failed to update profile. Please try again.');
        }
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render for admin users
  if (user?.role === 'ADMIN') {
    return (
      <div className="page-container">
        <Container maxWidth="sm">
          <Box sx={{ mb: 4, mt: 4 }}>
            <Alert severity="info">
              Admin users do not have profiles to update.
            </Alert>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Container maxWidth="sm">
        <Box sx={{ mb: 4, mt: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0', textAlign: 'center' }}>
            Update Your Profile
          </Typography>
        </Box>
        
        <Card className="card form-container">
          <CardContent sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
                  </Grid>
                )}
                
                {success && (
                  <Grid item xs={12}>
                    <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    label="Display Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    placeholder="Enter your display name"
                    helperText="This name will be displayed in the navigation bar"
                  />
                </Grid>
                
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    fullWidth
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PersonIcon />}
                    className="action-button"
                    sx={{ 
                      bgcolor: '#1565c0',
                      '&:hover': { bgcolor: '#0d47a1' },
                      py: 1.2
                    }}
                  >
                    {isLoading ? 'Updating...' : 'Update Profile'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}